import { Link } from '@tanstack/react-router'
import { ChevronLeftIcon } from 'lucide-react'
import ReactMarkdown, { type Components } from 'react-markdown'
import remarkGfm from 'remark-gfm'
import remarkDirective from 'remark-directive'
import { useTrackOnce } from '@/hooks/useTrackOnce'
import { trackArticleViewed } from '@/lib/track'
import type { Article } from '../types'
import { formatArticleDate, readingMinutes } from '../utils'
import { remarkDirectives } from '../markdown/remarkDirectives'
import { ArticleOffer } from './directives/ArticleOffer'
import { ArticleOffers } from './directives/ArticleOffers'
import { ArticleCta } from './directives/ArticleCta'

const remarkPlugins = [remarkGfm, remarkDirective, remarkDirectives]

// Markdown element overrides + embeddable directive components (see
// remarkDirectives). Directive elements (`nabidka`, `cta`) are not part of the
// HTML element map, so the object is cast to `Components`.
const markdownComponents = {
  // An image on its own line parses as a paragraph holding it; a <figure>
  // can't sit inside <p>, so such a paragraph renders without the wrapper.
  p: ({ node, children }) => {
    const onlyChild = node?.children.length === 1 ? node.children[0] : undefined
    return onlyChild?.type === 'element' && onlyChild.tagName === 'img' ? <>{children}</> : <p>{children}</p>
  },
  // Inline images: rounded, responsive, lazy-loaded. The alt text doubles as
  // the caption shown below the image.
  img: ({ src, alt }) =>
    typeof src === 'string' ? (
      <figure>
        <img src={src} alt={alt ?? ''} loading="lazy" className="w-full rounded-xl" />
        {alt ? <figcaption className="text-center text-sm text-slate-400">{alt}</figcaption> : null}
      </figure>
    ) : null,
  // ::nabidka{slug="..."} — single live accommodation card.
  nabidka: (props: { slug?: string }) => <ArticleOffer slug={props.slug} />,
  // ::nabidky{pets="true" region="..." ...} — random offers matching a filter.
  nabidky: (props: {
    region?: string
    label?: string
    beds?: string
    cribs?: string
    adult?: string
    pets?: string
    count?: string
    facility?: string
    category?: string
    text?: string
  }) => <ArticleOffers {...props} />,
  // ::cta{text="..." region="..." label="..."} — link to the offers listing.
  cta: (props: { text?: string; region?: string; label?: string; children?: React.ReactNode }) => (
    <ArticleCta text={props.text} region={props.region} label={props.label}>
      {props.children}
    </ArticleCta>
  ),
} as Components

export function ArticleContent({ article }: { article: Article }) {
  const { meta, content } = article

  useTrackOnce(() => trackArticleViewed(meta), [meta])

  return (
    <article className="container py-8 lg:py-12">
      <div className="mx-auto max-w-[820px]">
        <Link
          to="/inspirace"
          className="inline-flex items-center gap-1 text-sm font-medium text-slate-500 transition-colors hover:text-slate-900"
        >
          <ChevronLeftIcon className="h-4 w-4" />
          Zpět na inspiraci
        </Link>

        <div className="mt-6 aspect-[16/9] overflow-clip rounded-2xl">
          <img src={meta.coverImage} alt={meta.coverImageAlt} className="h-full w-full object-cover object-center" />
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs font-medium text-slate-400">
          <span>{formatArticleDate(meta.publishedAt)}</span>
          <span aria-hidden>•</span>
          <span>{readingMinutes(content)} min čtení</span>
        </div>

        <h1 className="mt-3 text-3xl font-medium text-slate-900 md:text-4xl">{meta.title}</h1>
        <p className="mt-4 text-lg text-slate-500">{meta.perex}</p>

        <div className="prose prose-slate prose-headings:font-medium prose-a:text-red prose-a:no-underline hover:prose-a:underline mt-8 max-w-none">
          <ReactMarkdown remarkPlugins={remarkPlugins} components={markdownComponents}>
            {content}
          </ReactMarkdown>
        </div>
      </div>
    </article>
  )
}
